/*
 * 戒导航 · 媒体存储模块（jiedh/media）—— 博客头图上传组件
 * =============================================================================
 * 作用：替换 fof/blog「文章设置」弹窗里原来的「头图 URL 输入框」，
 *      改为「选图 → 裁剪 → 上传」：选择本地图片 → 弹出 2:1 裁剪弹窗 → 出图（WebP）
 *      → 直传本站接口 → 落 R2（前缀 blog-covers/）→ 回填 URL。
 *
 * 为什么要加这一步裁剪（v2.6 需求，设计见 doc/图片存储R2对象存储设计方案.md 8.6）：
 *   头图在文章页 / 精选卡是 `background-size: cover` 显示的 —— 图片比例 ≠ 容器比例就必裁，
 *   水印又贴在右下/底部（被裁重灾区），用户因此看到「水印显示不完整」。
 *   本组件把出图比例锁死 2:1，配合 less/forum.less 把三处显示容器统一成 `aspect-ratio: 2/1`，
 *   比例恒等 ⇒ 零裁切 ⇒ 上传即所见。裁剪逻辑全在 BlogCoverCropperModal（原图不上传，只传成品）。
 *
 * 为什么要改（业务背景）：
 *   外链图片随时可能失效（第三方删图、防盗链、站点下线），一旦失效，文章头图就是破图，
 *   对访客非常不友好。改为「统一上传到自己的 R2」，图片生命周期由本站掌控，
 *   并且可在桶内用前缀分类管理（blog-covers/YYYY/MM/uuid.webp）。
 *
 * 接口契约（后端见 packages/media/src/Api/Controller/UploadBlogCoverController.php）：
 *   POST  {apiUrl}/jiedh-media/blog-cover
 *   Body  multipart/form-data，文件字段名固定为 `image`
 *   返回  200 JSON：{ "url": "https://<R2 自定义域>/blog-covers/YYYY/MM/<uuid>.webp" }
 *
 * 两个实现要点（踩坑记录）：
 *   1) FormData 上传必须写 `serialize: (raw) => raw`，否则 Flarum 会把 body 当普通对象
 *      序列化成 JSON（该写法与官方 fof/upload 的 Uploader 完全一致）。
 *   2) `host` 属性直接持有弹窗实例，头图值仍然写在弹窗的 `featuredImage` Stream 上，
 *      因此「保存」按钮走的还是 fof/blog 原有提交链路，不需要我们碰它的 submitData()。
 */

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';

/**
 * 头图 Stream 的最小接口（fof/blog 弹窗上的 `featuredImage`）。
 *
 * Stream 是可调用的：无参调用 = 读取当前值；传参调用 = 写入新值。
 * 这里刻意不 import fof/blog 的类型：该弹窗属于**惰性模块**（ext: 路径），
 * 只在我们真正扩展它时才被 webpack 运行时加载，静态 import 类型并不安全。
 */

export default class BlogCoverUploader extends Component {
  /** 隐藏的原生 file 输入框：由「上传/更换」按钮触发 click，避免各浏览器默认样式不一致 */

  /** 是否正在上传（用于按钮 loading / 禁用，防止重复提交） */
  uploading = false;

  /** 上传失败的提示文案；null = 无错误 */
  error = null;
  view() {
    const stream = this.attrs.host.featuredImage;
    const url = stream();
    return m("div", {
      className: "Form-group FoFBlog-ArticleImage"
    }, m("label", null, app.translator.trans('jiedh-media.forum.blog_cover.label'), ":"), m("input", {
      type: "file",
      accept: "image/*",
      className: "sr-only",
      "aria-label": app.translator.trans('jiedh-media.forum.blog_cover.cropper.choose'),
      oncreate: vnode => {
        this.input = vnode.dom;
      },
      onchange: event => this.onFileSelected(event)
    }), m("div", null, m(Button, {
      className: "Button",
      icon: "fas fa-cloud-upload-alt",
      loading: this.uploading,
      onclick: () => this.input.click()
    }, app.translator.trans(url ? 'jiedh-media.forum.blog_cover.change' : 'jiedh-media.forum.blog_cover.upload')), url !== '' && m(Button, {
      className: "Button",
      icon: "fas fa-trash-alt",
      style: {
        marginLeft: '8px'
      },
      disabled: this.uploading,
      onclick: () => this.remove()
    }, app.translator.trans('jiedh-media.forum.blog_cover.remove'))), m("small", null, app.translator.trans('jiedh-media.forum.blog_cover.tip')), m("small", null, app.translator.trans('jiedh-media.forum.blog_cover.hint')), this.uploading && m("div", {
      className: "helpText"
    }, app.translator.trans('jiedh-media.forum.blog_cover.uploading')), this.error !== null && m("div", {
      className: "helpText",
      style: {
        color: 'var(--alert-error-color, #d83e3e)'
      }
    }, this.error), url !== '' && m("img", {
      src: url,
      alt: "Article image",
      title: app.translator.trans('jiedh-media.forum.blog_cover.label'),
      style: {
        width: '100%',
        marginTop: '15px'
      }
    }));
  }

  /**
   * 用户选好文件后的入口。
   *
   * 注意：`accept="image/*"` 只是「文件选择器过滤」，用户可以手动切换成「所有文件」，
   * 所以这里必须再做一次类型硬校验（后端 BlogCoverValidator 还有更严格的一道校验）。
   *
   * v2.6：校验通过后不再直接上传，而是压栈打开 2:1 裁剪弹窗（体积上限等后续校验在弹窗内做）。
   */
  onFileSelected(event) {
    const input = event.target;
    const file = input.files && input.files[0];

    // 立即清空 input：允许用户连续两次选择同一个文件也能触发 change
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.error = String(app.translator.trans('jiedh-media.forum.blog_cover.invalid_type'));
      m.redraw();
      return;
    }
    this.error = null;
    this.openCropper(file);
  }

  /**
   * 压栈打开头图裁剪弹窗。
   *
   * ⚠️ 第三个参数 stackModal 必须传 true（与 nav 后台 LinkModal::openCropper 同一个坑）：
   *   核心 ModalManagerState.show() 的 stackModal 默认 false，语义是「覆盖」——会执行
   *   `this.modalList = [this.modal]` 把底下的「文章设置」弹窗直接踢出栈（DOM 卸载、
   *   用户已填的标题/正文全丢）；而裁剪弹窗完成后自己会 hide()，此时栈里只剩它一个，
   *   close() 会清空整栈。传 true 则改成 push，文章设置弹窗留在栈底不受影响。
   *
   * ⚠️ 第一个参数必须是「返回 { default: 类 } 的工厂」，动态引入天然满足；
   *   同时它让弹窗独立成 chunk，不给 forum.js 主包增加体积（裁剪库本体在 ../utils 那层再拆一次）。
   */
  openCropper(file) {
    app.modal.show(() => import(/* webpackChunkName: 'forum/components/BlogCoverCropperModal', webpackMode: 'lazy-once' */ './BlogCoverCropperModal'), {
      file,
      // 弹窗出图后回调：这里走原有上传链路（字段名 image、接口不变）。
      // 返回值语义：`true` = 成功（弹窗关闭）；字符串 = 失败原因（弹窗留窗原样展示）。
      onCropped: cropped => this.upload(cropped)
    }, true);
  }

  /**
   * 上传文件到 R2（经本站接口转发，前端不接触任何 R2 密钥）。
   *
   * @returns 成功返回 `true`；失败返回**可直接展示给用户的失败原因**。
   *          裁剪弹窗据此决定「关窗」还是「留在窗内展示这条原因让用户重试」——
   *          必须把后端 422 的 detail 带出去（例如「图片未通过内容安全审核…」），
   *          否则用户只看到笼统的「上传失败」，无从判断是审核被拒还是网络/服务异常。
   */
  async upload(file) {
    this.uploading = true;
    this.error = null;
    m.redraw();
    const body = new FormData();
    body.append('image', file);
    try {
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/jiedh-media/blog-cover',
        // ★ 关键：body 是 FormData，必须原样交给浏览器（不能走 Flarum 默认的 JSON 序列化），
        //   浏览器会自动带上 multipart/form-data; boundary=... 请求头。
        serialize: raw => raw,
        body,
        // 用组件内联提示代替全局弹窗，避免「弹窗里再弹一个全局错误」的重复提醒；
        // 但仍在控制台留痕，方便排查（对应开发规范「关键信息要有日志」）。
        errorHandler: error => {
          console.error('[jiedh-media] 博客头图上传失败', error);
        }
      });

      // 回填到弹窗的 featuredImage Stream（「保存」时由 fof/blog 原链路提交）
      this.attrs.host.featuredImage(response.url);
      return true;
    } catch (e) {
      const message = this.errorMessage(e);
      this.error = message;

      // 把具体原因一并交还给裁剪弹窗 —— 失败时用户真正盯着的是最上层那个弹窗，
      // 只写组件内联 error 的话会被压在弹窗底下，等于没提示。
      return message;
    } finally {
      this.uploading = false;
      m.redraw();
    }
  }

  /** 移除头图：清空 Stream（保存后即从文章上摘掉头图） */
  remove() {
    this.error = null;
    this.attrs.host.featuredImage('');
    m.redraw();
  }

  /**
   * 提取后端返回的错误信息。
   *
   * 后端校验失败走 Flarum 的 ValidationException（HTTP 422），
   * 错误正文形如 { errors: [{ detail: "..." }] }；其他异常（如网关 413）没有该结构时回退通用文案。
   */
  errorMessage(error) {
    const detail = error?.response?.errors?.[0]?.detail;
    return detail ? String(detail) : String(app.translator.trans('jiedh-media.forum.blog_cover.upload_failed'));
  }
}
flarum.reg.add('jiedh-media', 'forum/components/BlogCoverUploader', BlogCoverUploader);flarum.reg.addChunkModule('287', '877', 'jiedh-media', 'forum/components/BlogCoverCropperModal');
flarum.reg.addChunkModule('287', '877', 'jiedh-media', 'forum/utils/createCropper');